import type {
  ExtensionPackageDatabaseLink,
  ExtensionPackageSummary,
  ExtensionVersionSummary,
  OrganizationDatabase,
} from "@api-client";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { Select } from "@sylviq/ui-react-web/components/ui/select";
import { CodeEditor } from "@sylviq/ui-react-web/components/editor/CodeEditor";
import { useEffect, useState } from "react";
import {
  createOrganizationDatabase,
  linkExtensionPackageDatabase,
  listExtensionPackageDatabaseLinks,
  listOrganizationDatabases,
  unlinkExtensionPackageDatabase,
} from "@/lib/api";
import { ExtensionDocumentation } from "./ExtensionDocumentation";

type VersionState =
  | { status: "idle" | "loading" }
  | { status: "error"; message: string }
  | { status: "loaded"; items: ExtensionVersionSummary[] };

type DatabaseListState =
  | { status: "idle" | "loading" }
  | { status: "error"; message: string }
  | { status: "loaded"; items: OrganizationDatabase[] };

type LinkListState =
  | { status: "idle" | "loading" }
  | { status: "error"; message: string }
  | { status: "loaded"; items: ExtensionPackageDatabaseLink[] };

interface DatabaseDeclaration {
  staticString: string;
  manifestDatabase: string | null;
}

const STATIC_STRING = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;

export function CuratedExtensionDetails({
  organizationId,
  canManage = false,
  extensionPackage,
  versions,
  onLoadVersions,
}: {
  organizationId?: string;
  canManage?: boolean;
  extensionPackage: ExtensionPackageSummary;
  versions: VersionState;
  onLoadVersions: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState("");
  const [databases, setDatabases] = useState<DatabaseListState>({ status: "idle" });
  const [links, setLinks] = useState<LinkListState>({ status: "idle" });
  const [linkNotice, setLinkNotice] = useState<{
    tone: "status" | "alert";
    message: string;
  } | null>(null);
  const [loadRevision, setLoadRevision] = useState(0);
  const selectedVersion = versions.status === "loaded"
    ? versions.items.find((item) => item.id === selectedVersionId) ?? versions.items[0]
    : undefined;
  const name = `${extensionPackage.publisher}/${extensionPackage.key}`;
  const linkEditor = Boolean(canManage && organizationId);

  useEffect(() => {
    if (!open || !organizationId || !canManage) return;
    let cancelled = false;
    setDatabases({ status: "loading" });
    setLinks({ status: "loading" });
    setLinkNotice(null);
    void (async () => {
      const [databaseResult, linkResult] = await Promise.allSettled([
        listOrganizationDatabases(organizationId),
        listExtensionPackageDatabaseLinks(organizationId, extensionPackage.id),
      ]);
      if (cancelled) return;
      setDatabases(databaseResult.status === "fulfilled"
        ? { status: "loaded", items: databaseResult.value }
        : { status: "error", message: "Organization databases could not be loaded." });
      setLinks(linkResult.status === "fulfilled"
        ? { status: "loaded", items: linkResult.value }
        : { status: "error", message: "Database links could not be loaded." });
    })();
    return () => {
      cancelled = true;
    };
  }, [canManage, extensionPackage.id, loadRevision, open, organizationId]);

  function recordLink(link: ExtensionPackageDatabaseLink) {
    setLinks((current) => {
      if (current.status !== "loaded") return current;
      const next = current.items.filter(
        (candidate) => candidate.static_string !== link.static_string,
      );
      next.push(link);
      next.sort((left, right) => left.static_string.localeCompare(right.static_string));
      return { status: "loaded", items: next };
    });
    setLinkNotice({
      tone: "status",
      message: `${link.static_string} is linked to ${link.database.name}.`,
    });
  }

  function recordDatabase(database: OrganizationDatabase) {
    setDatabases((current) => {
      const items = current.status === "loaded" ? current.items : [];
      const next = items.filter((candidate) => candidate.id !== database.id);
      next.push(database);
      next.sort((left, right) => left.name.localeCompare(right.name));
      return { status: "loaded", items: next };
    });
    setLinkNotice({
      tone: "status",
      message: `${database.name} was created.`,
    });
  }

  function removeLink(staticString: string) {
    setLinks((current) => current.status === "loaded"
      ? {
          status: "loaded",
          items: current.items.filter(
            (candidate) => candidate.static_string !== staticString,
          ),
        }
      : current);
    setLinkNotice({
      tone: "status",
      message: `${staticString} is no longer linked.`,
    });
  }

  const declarations = selectedVersion
    ? databaseDeclarations(selectedVersion.manifest)
    : [];
  const linkByString = links.status === "loaded"
    ? new Map(links.items.map((link) => [link.static_string, link]))
    : new Map<string, ExtensionPackageDatabaseLink>();
  const databaseItems = databases.status === "loaded" ? databases.items : [];
  const hasLinkableDeclaration = declarations.some(
    (declaration) => !declaration.manifestDatabase,
  );

  return <>
    <Button aria-label={`Details for curated ${name}`} className="h-auto max-w-full break-all text-left" size="sm" variant="ghost" onClick={() => {
      setOpen(true);
      if (versions.status === "idle" || versions.status === "error") onLoadVersions();
    }}>{name}</Button>
    <Dialog open={open} onOpenChange={setOpen} title="Curated extension" description="Organization-owned package. Its drafts are edited separately in this organization; published releases are immutable.">
      <div className="max-h-[min(60dvh,calc(100dvh-16rem))] space-y-4 overflow-y-auto">
        <p className="break-all font-medium">{name}</p>
        <dl className="space-y-2 text-sm">
          <div><dt className="font-medium">Package</dt><dd>{extensionPackage.status} · {extensionPackage.visibility} · {extensionPackage.pricing}</dd></div>
          <div><dt className="font-medium">Authoring</dt><dd>Choose an organization draft below to edit source, manifest and binary fixtures. Saving or validating a draft does not install or run it.</dd></div>
        </dl>
        {versions.status === "loading" ? <p role="status">Loading release details…</p> : null}
        {versions.status === "error" ? <div className="space-y-2"><p role="alert">Release details could not be loaded.</p><Button onClick={onLoadVersions}>Retry release details</Button></div> : null}
        {versions.status === "loaded" && !versions.items.length ? <p>No recorded releases. Add summary and documentation to the draft manifest before requesting publication.</p> : null}
        {selectedVersion ? <>
          <Select aria-label="Release to inspect" value={selectedVersion.id} onChange={setSelectedVersionId} options={versions.status === "loaded" ? versions.items.map((item) => ({ value: item.id, label: `${item.semver} · ${item.state}` })) : []} />
          <p className="break-all text-sm">API {selectedVersion.api_version} · Bundle {selectedVersion.bundle_digest}</p>
          <ExtensionDocumentation publisher={extensionPackage.publisher} extensionKey={extensionPackage.key} metadata={selectedVersion.manifest} />
          {linkEditor ? (
            <section aria-label="Database links" className="space-y-3 border-t pt-4">
              <div className="space-y-1">
                <h3 className="font-semibold">Database links</h3>
                <p className="text-sm text-[var(--muted-foreground)]">
                  Link a published static string to a database in this organization. The installer does not choose or change these links.
                </p>
              </div>
              {databases.status === "loading" || links.status === "loading" ? (
                <p role="status" className="text-sm">Loading database links…</p>
              ) : null}
              {databases.status === "error" || links.status === "error" ? (
                <div className="space-y-2">
                  <p role="alert" className="text-sm text-[var(--danger)]">
                    {databases.status === "error"
                      ? databases.message
                      : links.status === "error"
                        ? links.message
                        : "Database links could not be loaded."}
                  </p>
                  <Button variant="outline" onClick={() => setLoadRevision((value) => value + 1)}>
                    Retry database links
                  </Button>
                </div>
              ) : null}
              {databases.status === "loaded" && links.status === "loaded" ? (
                selectedVersion.state !== "published" ? (
                  <p className="text-sm text-[var(--muted-foreground)]">
                    Publish this release before linking its declared databases.
                  </p>
                ) : declarations.length === 0 ? (
                  <p className="text-sm text-[var(--muted-foreground)]">
                    This release declares no databases.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {hasLinkableDeclaration ? (
                      <CreateOrganizationDatabase
                        existingNames={databaseItems.map((database) => database.name)}
                        organizationId={organizationId!}
                        onCreated={recordDatabase}
                        onError={(message) => setLinkNotice({ tone: "alert", message })}
                      />
                    ) : null}
                    {declarations.map((declaration) => {
                      const linked = linkByString.get(declaration.staticString);
                      if (declaration.manifestDatabase) {
                        return (
                          <div className="rounded-md border p-3 text-sm" key={declaration.staticString}>
                            <code className="break-all">{declaration.staticString}</code>
                            <p className="mt-1 text-[var(--muted-foreground)]">
                              Linked in the manifest to <code>{declaration.manifestDatabase}</code>.
                            </p>
                          </div>
                        );
                      }
                      return (
                        <DatabaseLinkRow
                          data={databaseItems}
                          declaration={declaration}
                          key={declaration.staticString}
                          linked={linked}
                          organizationId={organizationId!}
                          packageId={extensionPackage.id}
                          onLinked={recordLink}
                          onUnlinked={removeLink}
                          onError={(message) => setLinkNotice({ tone: "alert", message })}
                        />
                      );
                    })}
                  </div>
                )
              ) : null}
              {linkNotice ? (
                <p
                  className={`text-sm ${linkNotice.tone === "alert" ? "text-[var(--danger)]" : "text-[var(--muted-foreground)]"}`}
                  role={linkNotice.tone}
                >
                  {linkNotice.message}
                </p>
              ) : null}
            </section>
          ) : null}
          <section aria-label="Released manifest"><h3 className="font-semibold">Released manifest (read-only)</h3><CodeEditor ariaLabel="Released manifest" language="json" readOnly onChange={() => {}} value={JSON.stringify(selectedVersion.manifest, null, 2)} height="240px" /></section>
        </> : <ExtensionDocumentation publisher={extensionPackage.publisher} extensionKey={extensionPackage.key} metadata={{}} />}
      </div>
    </Dialog>
  </>;
}

function CreateOrganizationDatabase({
  existingNames,
  organizationId,
  onCreated,
  onError,
}: {
  existingNames: string[];
  organizationId: string;
  onCreated: (database: OrganizationDatabase) => void;
  onError: (message: string) => void;
}) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (busy) return;
    if (!STATIC_STRING.test(name)) {
      onError("Database name is invalid.");
      return;
    }
    if (existingNames.includes(name)) {
      onError("A database with this name already exists.");
      return;
    }
    setBusy(true);
    try {
      const created = await createOrganizationDatabase(organizationId, name);
      setName("");
      onCreated(created);
    } catch (cause) {
      onError(createError(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="space-y-2 rounded-md border p-3"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <label className="grid gap-1 text-sm" htmlFor="organization-database-name">
        Database name
        <Input
          aria-label="Database name"
          autoComplete="off"
          disabled={busy}
          id="organization-database-name"
          maxLength={64}
          spellCheck={false}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </label>
      <p className="text-sm text-[var(--muted-foreground)]">
        A letter, then letters, digits, or underscores. Extension code calls the linked database by its static string.
      </p>
      <Button aria-label="Create organization database" disabled={busy} size="sm" type="submit">
        {busy ? "Creating…" : "Create database"}
      </Button>
    </form>
  );
}

function DatabaseLinkRow({
  data,
  declaration,
  linked,
  organizationId,
  packageId,
  onLinked,
  onUnlinked,
  onError,
}: {
  data: OrganizationDatabase[];
  declaration: DatabaseDeclaration;
  linked: ExtensionPackageDatabaseLink | undefined;
  organizationId: string;
  packageId: string;
  onLinked: (link: ExtensionPackageDatabaseLink) => void;
  onUnlinked: (staticString: string) => void;
  onError: (message: string) => void;
}) {
  const [selected, setSelected] = useState(linked?.database.id ?? "");
  const [busy, setBusy] = useState<"link" | "unlink" | null>(null);

  useEffect(() => {
    setSelected(linked?.database.id ?? "");
  }, [linked?.database.id]);

  const save = async () => {
    if (!selected || busy !== null) return;
    setBusy("link");
    try {
      onLinked(await linkExtensionPackageDatabase(
        organizationId,
        packageId,
        declaration.staticString,
        selected,
      ));
    } catch (cause) {
      onError(linkError(cause, "link"));
    } finally {
      setBusy(null);
    }
  };

  const unlink = async () => {
    if (!linked || busy !== null) return;
    setBusy("unlink");
    try {
      await unlinkExtensionPackageDatabase(
        organizationId,
        packageId,
        declaration.staticString,
      );
      onUnlinked(declaration.staticString);
    } catch (cause) {
      onError(linkError(cause, "unlink"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-2 rounded-md border p-3">
      <code className="break-all text-sm">{declaration.staticString}</code>
      <div className="flex flex-wrap items-center gap-2">
        <Select
          aria-label={`Database for ${declaration.staticString}`}
          value={selected}
          onChange={setSelected}
          options={data.map((database) => ({
            value: database.id,
            label: database.name,
          }))}
          placeholder="Choose database"
        />
        {linked ? (
          <span className="text-sm text-[var(--muted-foreground)]">
            Currently {linked.database.name}
          </span>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          aria-label={`${linked ? "Update" : "Save"} database link for ${declaration.staticString}`}
          disabled={!selected || busy !== null || selected === linked?.database.id}
          onClick={() => void save()}
          size="sm"
        >
          {busy === "link" ? "Saving…" : linked ? "Update" : "Save"}
        </Button>
        {linked ? (
          <Button
            aria-label={`Unlink ${declaration.staticString}`}
            disabled={busy !== null}
            onClick={() => void unlink()}
            size="sm"
            variant="outline"
          >
            {busy === "unlink" ? "Unlinking…" : "Unlink"}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function databaseDeclarations(value: unknown): DatabaseDeclaration[] {
  if (!value || typeof value !== "object") return [];
  const rows = (value as { databases?: unknown }).databases;
  if (!Array.isArray(rows)) return [];
  const byName = new Map<string, DatabaseDeclaration>();
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    const entry = row as { name?: unknown; database?: unknown };
    if (typeof entry.name !== "string" || !STATIC_STRING.test(entry.name)) continue;
    const manifestDatabase = typeof entry.database === "string" && STATIC_STRING.test(entry.database)
      ? entry.database
      : null;
    const existing = byName.get(entry.name);
    if (!existing || manifestDatabase) {
      byName.set(entry.name, {
        staticString: entry.name,
        manifestDatabase,
      });
    }
  }
  return [...byName.values()].sort((left, right) =>
    left.staticString.localeCompare(right.staticString)
  );
}

function createError(cause: unknown): string {
  const status = typeof cause === "object" && cause !== null && "status" in cause
    ? (cause as { status?: unknown }).status
    : null;
  const code = typeof cause === "object" && cause !== null && "code" in cause
    ? (cause as { code?: unknown }).code
    : null;
  if (status === 409 && code === "database_exists") {
    return "A database with this name already exists.";
  }
  if (status === 400 && code === "invalid_database_name") {
    return "Database name is invalid.";
  }
  if (status === 403) return "You cannot create a database for this organization.";
  return "The database could not be created.";
}

function linkError(cause: unknown, action: "link" | "unlink"): string {
  const status = typeof cause === "object" && cause !== null && "status" in cause
    ? (cause as { status?: unknown }).status
    : null;
  const code = typeof cause === "object" && cause !== null && "code" in cause
    ? (cause as { code?: unknown }).code
    : null;
  if (status === 403) return "Only the organization owner can manage database links.";
  if (action === "link" && status === 409 && code === "manifest_link_exists") {
    return "This static string is already linked in the manifest.";
  }
  if (action === "link" && status === 404 && code === "static_string_not_declared") {
    return "Publish a release that declares this static string first.";
  }
  if (action === "link" && status === 404 && code === "database_not_found") {
    return "That database is no longer available in this organization.";
  }
  return action === "link"
    ? "The database link could not be saved."
    : "The database link could not be removed.";
}
